import { HeaderContext } from '@tanstack/table-core';
import { InstallmentStatus } from 'assets/js/pages/pledges/usePledgeForm';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { Pledge } from 'types/domain';

type PledgesTableKey = 'contact_name' | 'frequency' | keyof Pledge;

const curriedTransformTableKeyToReadable = (header: HeaderContext<Pledge, unknown>) =>
    transformTableKeyToReadable(header.column.id as PledgesTableKey);

const transformTableKeyToReadable = (key: PledgesTableKey) => {
    switch (key) {
        case 'contact_name':
            return 'Contact';
        case 'frequency':
            return 'Type';
        default:
            return pledgeKeyToReadable(key);
    }
};

const pledgeKeyToReadable = (key: keyof Pledge) => {
    switch (key) {
        case 'installment_frequency':
            return 'Frequency';
        case 'amount':
            return 'Pledge amount';
        case 'amount_remaining':
            return 'Remaining balance';
        case 'status_friendly':
            return 'Status';
        case 'next_installment_date':
            return 'Next installment';
        case 'fulfilled_at':
            return 'Fulfillment date';
        default:
            return capitalize(key.split('_').join(' '));
    }
};

const addInstallmentStatus = <T extends { date: DateTime; amount: number }>(
    installments: T[],
    totalFulfilled: number = 0,
) => {
    const today = DateTime.now().endOf('day');
    let cumulativeAmount = 0;
    let activeMarked = false;

    return installments
        .map((item, index) => {
            cumulativeAmount += item.amount;

            let status = InstallmentStatus.Upcoming;

            if (totalFulfilled >= cumulativeAmount) {
                status = InstallmentStatus.Paid;
            } else if (item.date < today) {
                status = InstallmentStatus.Late;
            } else if (!activeMarked && item.date >= today) {
                activeMarked = true;
                status = InstallmentStatus.Active;
            }

            return {
                ...item,
                number: index + 1,
                status,
            };
        })
        .toSorted((a, b) => {
            const current = a.date;
            const next = b.date;
            if (current < next) {
                return -1;
            } else if (current > next) {
                return 1;
            } else {
                return 0;
            }
        });
};

export {
    curriedTransformTableKeyToReadable,
    transformTableKeyToReadable,
    pledgeKeyToReadable,
    addInstallmentStatus,
};
export type { PledgesTableKey };
